import { SEARCH_DESTINATIONS } from '../config/commands';
import type { SearchDestination } from '../config/commands';
import type { ClientResponse, SymbolMatch } from '@biasafe.models';
import {
  groupInstruments,
  instrumentDisplayName,
} from '@/features/Instruments/instrumentMetadata';

export type SearchScope =
  'all' | 'functions' | 'clients' | 'strategies' | 'securities';
export type SearchItem =
  | {
      kind: 'command';
      key: string;
      label: string;
      code: string;
      destination: SearchDestination;
    }
  | {
      kind: 'client';
      key: string;
      label: string;
      code: string;
      client: ClientResponse;
    }
  | {
      kind: 'strategy';
      key: string;
      label: string;
      code: string;
      strategyId: string;
    }
  | {
      kind: 'security';
      key: string;
      label: string;
      code: string;
      instrument: SymbolMatch;
      children: Array<SymbolMatch>;
      child?: boolean;
    };
export type SearchReference = {
  kind: SearchItem['kind'];
  id: string;
  instrument?: SymbolMatch;
};
export const normalizeSearch = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
/** Same match tiers across all categories; shorter word completion breaks prefix ties. */
export function relevance(item: SearchItem, query: string): number {
  const q = normalizeSearch(query),
    code = normalizeSearch(item.code),
    label = normalizeSearch(item.label);
  if (!q) return 0;
  if (code === q) return 0;
  if (label === q) return 10;
  const word = label.split(/[^\p{L}\p{N}]+/u).find((w) => w.startsWith(q));
  if (code.startsWith(q)) return 20 + Math.min(9, code.length - q.length) / 10;
  if (word) return 20 + Math.min(9, word.length - q.length) / 10;
  if (label.includes(q) || code.includes(q)) return 30;
  return Infinity;
}
export function displaySecurityName(instrument: SymbolMatch): string {
  const name = instrumentDisplayName(instrument);
  if (name !== name.toUpperCase()) return name;
  const keep = new Set([
    'ETF',
    'ETN',
    'UCITS',
    'CDR',
    'ADR',
    'DR',
    'ESG',
    'USD',
    'CAD',
    'EUR',
    'BMO',
    'AMD',
    'IBM',
    'US',
    'UK',
    'USA',
    'NV',
    'SA',
    'SE',
    'AG',
    'LLC',
    'LP',
    'LS',
  ]);
  return name.replace(/[\p{L}\p{N}]+/gu, (word) =>
    keep.has(word) || /\d/.test(word)
      ? word
      : word[0] + word.slice(1).toLowerCase(),
  );
}
export function securityItem(
  instrument: SymbolMatch,
  children: Array<SymbolMatch> = [],
  child = false,
): Extract<SearchItem, { kind: 'security' }> {
  return {
    kind: 'security',
    key: `security:${instrument.conid}`,
    label: displaySecurityName(instrument),
    code: instrument.symbol,
    instrument,
    children,
    child,
  };
}
export function buildSearchItems(
  clients: Array<ClientResponse>,
  strategies: Array<{ id: string; name: string; code?: string | null }>,
  instruments: Array<SymbolMatch>,
): Array<SearchItem> {
  return [
    ...SEARCH_DESTINATIONS.map((destination) => ({
      kind: 'command' as const,
      key: `command:${destination.code}`,
      label: destination.label,
      code: destination.code,
      destination,
    })),
    ...clients.map((client) => ({
      kind: 'client' as const,
      key: `client:${client.id}`,
      label: client.displayName || client.referenceCode,
      code: client.referenceCode,
      client,
    })),
    ...strategies.map((strategy) => ({
      kind: 'strategy' as const,
      key: `strategy:${strategy.id}`,
      label: strategy.name,
      code: strategy.code || '',
      strategyId: strategy.id,
    })),
    ...groupInstruments(
      instruments.filter(
        (i) => i.conid && i.securityType && i.securityType !== 'Unknown',
      ),
    ).map((group) => securityItem(group.matches[0], group.matches.slice(1))),
  ];
}
export const scopeKind: Record<
  Exclude<SearchScope, 'all'>,
  SearchItem['kind']
> = {
  functions: 'command',
  clients: 'client',
  strategies: 'strategy',
  securities: 'security',
};
const tieOrder = { command: 0, client: 1, strategy: 2, security: 3 };
export function rankItems(
  items: Array<SearchItem>,
  query: string,
): Array<SearchItem> {
  // IBKR has its own fuzzy/name ranking: retain its non-exact results after local exact tiers.
  return items
    .map((item) => ({ item, score: relevance(item, query) }))
    .filter(
      ({ item, score }) => item.kind === 'security' || Number.isFinite(score),
    )
    .map(({ item, score }) => ({
      item,
      score: score === Infinity ? 40 : score,
    }))
    .sort(
      (a, b) =>
        a.score - b.score || tieOrder[a.item.kind] - tieOrder[b.item.kind],
    )
    .map(({ item }) => item);
}
export function searchReference(item: SearchItem): SearchReference {
  if (item.kind === 'security') {
    const i = item.instrument;
    return {
      kind: item.kind,
      id: String(i.conid),
      instrument: {
        symbol: i.symbol,
        conid: i.conid,
        name: i.name,
        description: i.description,
        securityType: i.securityType,
      },
    };
  }
  return {
    kind: item.kind,
    id:
      item.kind === 'command'
        ? item.code
        : item.kind === 'client'
          ? item.client.id
          : item.strategyId,
  };
}
/** Saved private entities are IDs only and are re-resolved against the authorized directory. */
export function resolveReference(
  ref: SearchReference,
  items: Array<SearchItem>,
): SearchItem | undefined {
  if (
    ref.kind === 'security' &&
    ref.instrument &&
    Number(ref.id) > 0 &&
    ref.instrument.conid === Number(ref.id)
  )
    return securityItem(ref.instrument);
  return items.find(
    (i) => i.kind === ref.kind && searchReference(i).id === ref.id,
  );
}
export function referenceKey(ref: SearchReference) {
  return `${ref.kind}:${ref.id}`;
}
export function parseSavedSearch(value: string | null): {
  recent: Array<SearchReference>;
  favorites: Array<SearchReference>;
} {
  const valid = (list: unknown): Array<SearchReference> => {
    if (!Array.isArray(list)) return [];
    const result: Array<SearchReference> = [];
    for (const r of list.slice(0, 24)) {
      if (
        !r ||
        typeof r.id !== 'string' ||
        r.id.length > 100 ||
        !['command', 'client', 'strategy', 'security'].includes(r.kind)
      )
        continue;
      if (r.kind === 'security') {
        const i = r.instrument;
        if (
          !i ||
          typeof i.symbol !== 'string' ||
          i.symbol.length > 40 ||
          !Number.isSafeInteger(i.conid) ||
          i.conid <= 0 ||
          String(i.conid) !== r.id
        )
          continue;
        const text = (v: unknown) =>
          typeof v === 'string' ? v.slice(0, 200) : undefined;
        result.push({
          kind: 'security',
          id: r.id,
          instrument: {
            symbol: i.symbol,
            conid: i.conid,
            name: text(i.name),
            description: text(i.description),
            securityType: text(i.securityType),
          },
        });
      } else result.push({ kind: r.kind, id: r.id });
    }
    return result
      .filter(
        (r, index) =>
          result.findIndex(
            (other) => referenceKey(other) === referenceKey(r),
          ) === index,
      )
      .slice(0, 12);
  };
  try {
    const data = JSON.parse(value || '{}');
    return {
      recent: valid(data?.recent).slice(0, 8),
      favorites: valid(data?.favorites),
    };
  } catch {
    return { recent: [], favorites: [] };
  }
}
